import * as _ from 'lodash';
import * as moment from 'moment';
import { ICenterNotification } from '@nkr/models/notifications';
import { useState } from 'react';
import { Button, FormControl, FormGroup, Glyphicon, HelpBlock, Table } from 'react-bootstrap';
import ReactHtmlParser from 'react-html-parser';

interface IProps {
    data: ICenterNotification[],
    showLoading: boolean,
    onMarkRead: (id: number) => void,
}

const Notifications = (props: IProps) => {
    const [expanded, setExpanded] = useState(true);
    const [groupSize, setGroupSize] = useState(3);
    const [sortBy, setSortBy] = useState('addedDateTime');
    const [sortDirection, setSortDirection] = useState('desc');

    const [initialsById, setInitialsById] = useState<Record<number, string>>({});
    const [touchedById, setTouchedById] = useState<Record<number, boolean>>({});

    const handleInitialsChanged = (e: React.FormEvent<FormControl>) => {
        const input = e.currentTarget as unknown as HTMLInputElement;

        const id = Number(input.dataset.id);
        const value = input.value;

        setInitialsById(prev => ({ ...prev, [id]: value }));
    };

    const handleAcknowledge = (id: number) => {
        setTouchedById(prev => ({ ...prev, [id]: true }));

        const initials = initialsById[id]?.trim() ?? '';

        if (initials.length < 2) {
            return;
        }

        props.onMarkRead(id);
    };

    const handleShow = () => {
        if (groupSize == 3) {
            setGroupSize(props.data.length);
        } else {
            setGroupSize(3);
        }
    };

    const handleSort = (column: string) => {
        if (sortBy != column) {
            setSortBy(column);
            setSortDirection('asc');
        } else {
            if (sortDirection == 'asc') {
                setSortDirection('desc');
            } else {
                setSortBy('');
                setSortDirection('asc');
            }
        }
    };

    const getSortedData = () => {
        let data = _.orderBy(props.data, item => {
            if (sortBy == 'addedDateTime') {
                return moment(item.addedDateTime, 'MM/DD/YYYY HH:mm').toDate();
            }

            return item.description;
        }, [sortDirection]);

        return data;
    };

    const renderSortIndicator = (column: string) => {
        if (sortBy == column) {
            return <Glyphicon glyph={sortDirection == 'asc' ? 'triangle-top' : 'triangle-bottom'} />;
        }

        return null
    };

    const { data, showLoading } = props;

    const sortedData = getSortedData();
    const first = _.take(sortedData, groupSize);
    const remainingCount = sortedData.length - first.length;

    return (
        <>
            <div className='section-notifications' style={{ marginBottom: 20 }}>
                <div className='title-container'>
                    <div className='title' style={{ backgroundColor: showLoading ? '#eceaea' : null }} onClick={() => setExpanded(!expanded)}>
                        <h4 style={{ marginRight: 5 }}>NOTIFICATIONS <span>({data.length})</span></h4>
                        <span style={{ paddingLeft: 5, color: 'white' }}><Glyphicon glyph={expanded ? 'chevron-down' : 'chevron-up'} /></span>
                    </div>
                </div>

                {expanded ?
                    <div className='content' style={{ padding: 20, backgroundColor: showLoading ? '#eceaea' : null }}>
                        {showLoading && (
                            <div className="loader"><div className='message'>Checking for notifications, one moment please...</div></div>
                        )}

                        <div>
                            <Table className='notification-table' bordered striped style={{ marginBottom: 0 }}>
                                <thead>
                                    <tr style={{ backgroundColor: '#ddd' }}>
                                        <th style={{ width: '10%', cursor: 'default', borderRight: '1px solid white' }} onClick={() => handleSort('addedDateTime')}>Date {renderSortIndicator('addedDateTime')}</th>
                                        <th style={{ width: '25%', cursor: 'default', borderRight: '1px solid white' }} onClick={() => handleSort('description')}>Description {renderSortIndicator('description')}</th>
                                        <th style={{ borderRight: '1px solid white' }}>Notification</th>
                                        <th style={{ borderRight: '1px solid white' }}>Initials</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {first.map(item => {
                                        const initials = initialsById[item.id] ?? '';
                                        const touched = touchedById[item.id];
                                        const isInvalid = touched && initials.trim().length < 2;

                                        return (
                                            <tr key={item.id}>
                                                <td>{item.addedDateTime}</td>
                                                <td>{item.description}</td>
                                                <td>{ReactHtmlParser(item.notificationText)}</td>
                                                <td>
                                                    <FormGroup
                                                        validationState={isInvalid ? 'error' : undefined}
                                                        style={{ marginBottom: 0 }}
                                                    >
                                                        <FormControl
                                                            type="text"
                                                            value={initialsById[item.id] || ''}
                                                            maxLength={5}
                                                            placeholder="Initials"
                                                            bsSize="sm"
                                                            style={{ width: '50%' }}

                                                            data-id={item.id}          // 👈 critical
                                                            onChange={handleInitialsChanged}
                                                        />

                                                        {isInvalid && (
                                                            <HelpBlock>
                                                                At least two characters required.
                                                            </HelpBlock>
                                                        )}
                                                    </FormGroup>
                                                </td>

                                                <td style={{ textAlign: 'center' }}>
                                                    <Button
                                                        bsStyle="primary"
                                                        bsSize="xs"
                                                        onClick={() => handleAcknowledge(item.id)}
                                                    >
                                                        Acknowledge
                                                    </Button>
                                                </td>
                                            </tr>
                                        );
                                    })}

                                    {props.data.length === 0 && (
                                        <tr>
                                            <td colSpan={5}>
                                                At this time, there are no notifications
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>

                            {sortedData.length > 3 && (
                                <div style={{ textAlign: 'right', paddingTop: 5, paddingRight: 10 }}>
                                    <a style={{ cursor: 'default' }} onClick={handleShow}>
                                        {groupSize == 3 ?
                                            <>... {remainingCount} more</>
                                            :
                                            <>... show top 3</>
                                        }
                                    </a>
                                </div>
                            )}
                        </div>
                    </div>
                    :
                    <div className='content collapsed'></div>
                }
            </div>
        </>
    );
}

export default Notifications;
